import { AnnouncementDetailTable } from "../../../Announcements/AnnouncementsDetail";
import { genericEntityConstants } from "../../../api/GenericConstants";
import { ApiKeyDetailsTable } from "../../../ApiKeyManager/ApiKeyWidget";
import { AttachmentsDetailTable } from "../../../Attachments/AttachmentsDetail";
import { AuditLogsDetailTable } from "../../../AuditLog/AuditLogsDetail";
import { NotAllowed } from "../../../common/misc/UIconstants";
import { useEntityPermissions } from "../../../common/permissions/useEntityPermissions";
import { CustomFieldsDetailTable } from "../../../Customization/CustomFields/CustomFieldsDetailView";
import { CustomTypeDetailTable } from "../../../Customization/CustomTypes/CustomTypeDetail";
import { DatasetsDetailTable } from "../../../Dataset/DatasetsDetailTable";
import { LabNotebookEntryDetailContentTable } from "../../../ELN/LabNotebookEntries/LabNotebookEntryDetailContentTable";
import { LabNotebookExperimentDetailContentTable } from "../../../ELN/LabNotebookExperiments/LabNotebookExperimentDetailContentTable";
import { LabNotebooksDetailTable } from "../../../ELN/LabNotebooks/LabNotebookDetailView";
import { LabNotebookTemplatesDetailTable } from "../../../ELN/LabNotebookTemplates/LabNotebookTemplatesDetail";
import { InventoryItemsDetailTable } from "../../../Inventories/InventoryItemsDetail";
import { MethodsDetailTable } from "../../../Methods/MethodsDetail";
import { PersonDetailTable } from "../../../Persons/PersonsDetail";
import { ProjectsDetailTable } from "../../../Projects/ProjectsDetail";
import { RolesDetailTable } from "../../../Roles/RolesDetail";
import { SampleDetailTable } from "../../../samples/SamplesDetail";
import { GenericEntity, GenericEntityComponentProps } from "../GenericComponents";

export interface GenericEntityDetailTableProps {}

export const GenericEntityDetailTable = <
  Entity extends GenericEntity,
  ComponentProps extends GenericEntityDetailTableProps
>({
  entityTypeId,
  entity,
  componentProps,
}: GenericEntityComponentProps<Entity, ComponentProps>) => {
  const { canRead } = useEntityPermissions({ entityTypeId });
  const { entityConstants, fieldLabels } = genericEntityConstants[entityTypeId];
  if (!entity) return null;
  if (!canRead(entity)) return NotAllowed;
  switch (entityTypeId) {
    case "announcements":
      return (
        <AnnouncementDetailTable
          {...componentProps}
          entityTypeId={entityTypeId}
          entity={entity as any}
          entityConstants={entityConstants}
          fieldLabels={fieldLabels as any}
        />
      );
    case "apiKeys":
      return <ApiKeyDetailsTable apiKey={entity as any} disableCopyButton />;
    case "attachments":
      return (
        <AttachmentsDetailTable
          {...componentProps}
          entityTypeId={entityTypeId}
          entity={entity as any}
          entityConstants={entityConstants}
          fieldLabels={fieldLabels as any}
        />
      );
    case "auditLogs":
      return (
        <AuditLogsDetailTable
          {...componentProps}
          entityTypeId={entityTypeId}
          entity={entity as any}
          entityConstants={entityConstants}
          fieldLabels={fieldLabels as any}
        />
      );
    case "customFields":
      return (
        <CustomFieldsDetailTable
          {...componentProps}
          entityTypeId={entityTypeId}
          entity={entity as any}
          entityConstants={entityConstants}
          fieldLabels={fieldLabels as any}
        />
      );
    case "customTypes":
      return (
        <CustomTypeDetailTable
          {...componentProps}
          entityTypeId={entityTypeId}
          entity={entity as any}
          entityConstants={entityConstants}
          fieldLabels={fieldLabels as any}
        />
      );
    case "datasets":
      return (
        <DatasetsDetailTable
          {...componentProps}
          entityTypeId={entityTypeId}
          entity={entity as any}
          entityConstants={entityConstants}
          fieldLabels={fieldLabels as any}
        />
      );
    case "inventoryItems":
      return (
        <InventoryItemsDetailTable
          {...componentProps}
          entityTypeId={entityTypeId}
          entity={entity as any}
          entityConstants={entityConstants}
          fieldLabels={fieldLabels as any}
        />
      );
    case "methods":
      return (
        <MethodsDetailTable
          {...componentProps}
          entityTypeId={entityTypeId}
          entity={entity as any}
          entityConstants={entityConstants}
          fieldLabels={fieldLabels as any}
        />
      );
    case "notebookEntries":
      return (
        <LabNotebookEntryDetailContentTable
          {...componentProps}
          entityTypeId={entityTypeId}
          entity={entity as any}
          entityConstants={entityConstants}
          fieldLabels={fieldLabels as any}
        />
      );
    case "notebookExperiments":
      return (
        <LabNotebookExperimentDetailContentTable
          {...componentProps}
          entityTypeId={entityTypeId}
          entity={entity as any}
          entityConstants={entityConstants}
          fieldLabels={fieldLabels as any}
        />
      );
    case "notebookTemplates":
      return (
        <LabNotebookTemplatesDetailTable
          {...componentProps}
          entityTypeId={entityTypeId}
          entity={entity as any}
          entityConstants={entityConstants}
          fieldLabels={fieldLabels as any}
        />
      );
    case "notebooks":
      return (
        <LabNotebooksDetailTable
          {...componentProps}
          entityTypeId={entityTypeId}
          entity={entity as any}
          entityConstants={entityConstants}
          fieldLabels={fieldLabels as any}
        />
      );
    case "persons":
      return (
        <PersonDetailTable
          {...componentProps}
          entityTypeId={entityTypeId}
          entity={entity as any}
          entityConstants={entityConstants}
          fieldLabels={fieldLabels as any}
        />
      );
    case "projects":
      return (
        <ProjectsDetailTable
          {...componentProps}
          entityTypeId={entityTypeId}
          entity={entity as any}
          entityConstants={entityConstants}
          fieldLabels={fieldLabels as any}
        />
      );
    case "samples":
      return (
        <SampleDetailTable
          {...componentProps}
          entityTypeId={entityTypeId}
          entity={entity as any}
          entityConstants={entityConstants}
          fieldLabels={fieldLabels as any}
        />
      );
    case "roles":
      return (
        <RolesDetailTable
          {...componentProps}
          entityTypeId={entityTypeId}
          entity={entity as any}
          entityConstants={entityConstants}
          fieldLabels={fieldLabels as any}
        />
      );
    default:
      return null;
  }
};
